import React, { useContext, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle2, ShieldCheck, Store } from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import AuthContext from '../context/AuthContext';
import { PrimaryButton } from '../atelier';
// Live store snapshots reused from the theme gallery (scroll on hover).
import { SNAP as snapUrl } from '../utils/brandAssets';
const SHOWCASE = [
  { id: 16, name: 'VIVAHA', tag: 'Bridal couture' },
  { id: 9, name: 'ZEVAR', tag: 'Fine jewellery' },
  { id: 18, name: 'KINETIC', tag: 'Athleisure' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginUser, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  if (user) {
    // If kicked here via a 401 interceptor with ?redirect=<orig path>, restore
    // the user there post-login. Only same-origin paths (open-redirect guard).
    const redirect = searchParams.get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/homepage';
    return <Navigate to={safeRedirect} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await loginUser(e);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldWrap =
    'flex items-center gap-3 rounded-xl border border-line bg-white px-4 h-[52px] transition-colors focus-within:border-primary';
  const inputCls = 'w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400';

  return (
    <div className="min-h-screen overflow-x-hidden bg-bone text-ink">
      <UniversalNavbar />

      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-stretch gap-0 px-4 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* ───────────── Left: form ───────────── */}
        <div className="flex items-center justify-center py-12 lg:py-0">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
              The designer portal
            </p>
            <h1 className="mt-3 font-fell text-4xl font-semibold leading-[1.05] text-ink sm:text-[2.9rem]">
              Welcome back to the studio.
            </h1>
            <p className="mt-3 text-[15.5px] text-muted">
              Manage products, orders and analytics — all in one dashboard.
            </p>

            <div className="mt-8 rounded-2xl border border-line bg-white p-6 shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)] sm:p-8">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13.5px] font-semibold text-red-700"
                  role="alert"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-[13px] font-bold text-ink">
                    Email address
                  </label>
                  <div className={fieldWrap}>
                    <Mail size={18} className="flex-shrink-0 text-muted" />
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="password" className="block text-[13px] font-bold text-ink">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-[13px] font-semibold text-primary transition-opacity hover:opacity-70">
                      Forgot?
                    </Link>
                  </div>
                  <div className={fieldWrap}>
                    <Lock size={18} className="flex-shrink-0 text-muted" />
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      name="password"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="text-gray-400 transition-colors hover:text-gray-600"
                      aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                    >
                      {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold text-muted">
                  <input type="checkbox" name="remember-me" className="h-4 w-4 rounded accent-[#D45614]" />
                  Keep me signed in
                </label>

                {/* Submit — THE orange on this screen */}
                <PrimaryButton type="submit" disabled={isLoading} className="w-full">
                  {isLoading ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </PrimaryButton>
              </form>

              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" />
                <span className="text-[12.5px] font-semibold text-muted">New to Vasstr?</span>
                <span className="h-px flex-1 bg-line" />
              </div>

              <Link
                to="/Register"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-line bg-white px-6 py-3.5 text-[15px] font-semibold text-ink transition-colors hover:border-gray-300"
              >
                Open your studio free
              </Link>
            </div>

            <p className="mt-6 text-center text-[13px] text-muted">
              Need help?{' '}
              <a href="mailto:team@vasstr.com" className="font-semibold text-primary transition-opacity hover:opacity-70">
                team@vasstr.com
              </a>{' '}
              · +91 97043 87878
            </p>
          </motion.div>
        </div>

        {/* ───────────── Right: brand showcase (desktop) ───────────── */}
        <div className="relative hidden lg:flex">
          <div className="relative my-10 flex w-full items-center overflow-hidden rounded-[2.4rem] bg-ink">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(50% 40% at 25% 15%, rgba(212,86,20,0.30), transparent 70%), radial-gradient(45% 45% at 90% 80%, rgba(212,86,20,0.16), transparent 70%)',
              }}
            />
            <Store aria-hidden className="pointer-events-none absolute -right-10 -top-8 h-56 w-56 text-white opacity-5" />

            <div className="relative z-10 w-full px-12 py-14">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-[12.5px] font-semibold text-white">
                <ShieldCheck className="h-3.5 w-3.5" /> Your store, your customers, your revenue
              </span>
              <h2 className="mt-6 max-w-md font-fell text-[2.6rem] font-semibold leading-[1.08] text-white">
                Pick up right where you left off.
              </h2>
              <p className="mt-4 max-w-md text-[15.5px] leading-relaxed text-gray-400">
                27 beautiful designs, built-in payments, and everything you need to run your brand online.
              </p>

              <div className="mt-9 grid grid-cols-3 gap-4">
                {SHOWCASE.map((t) => (
                  <div key={t.id} className="group overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                    <div
                      className="h-40 w-full bg-no-repeat bg-top transition-[background-position] duration-[5000ms] ease-linear group-hover:bg-bottom"
                      style={{ backgroundImage: `url(${snapUrl(t.id)})`, backgroundSize: '100% auto' }}
                      aria-label={`${t.name} store design`}
                    />
                    <div className="px-3 py-2.5">
                      <span className="block font-fell text-[13.5px] font-semibold text-white">{t.name}</span>
                      <span className="text-[11px] font-semibold text-gray-500">{t.tag}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2.5">
                {['Keep 97% of every sale', 'Live in under 30 minutes', 'No marketplace commission'].map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13.5px] font-semibold text-white">
                    <CheckCircle2 className="h-4 w-4 text-gray-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
